(() => {
  /** @type {any} */
  const cfg = window.__DATALAYER_EVENTS__;
  if (!cfg || cfg.enabled !== true) return;

  window.dataLayer = window.dataLayer || [];

  const state = {
    /** @type {any | null} */
    cart: cfg.cart && Array.isArray(cfg.cart.items) ? cfg.cart : null,
  };

  const isWishlistPage =
    window.location.pathname.toLowerCase().includes('wishlist') ||
    cfg.template === 'page.swym' ||
    cfg.template === 'page.wishlist';

  const debug = cfg.debug === true;
  const itemIdField = cfg.itemIdField === 'sku' ? 'sku' : 'variant_id';
  const sendCustomerId = cfg.sendCustomerId === true;
  const format = cfg.format === 'ua' || cfg.format === 'both' ? cfg.format : 'ga4';
  const gtagMirror = cfg.gtagMirror === true;

  function dlog(...args) {
    if (!debug) return;
    // eslint-disable-next-line no-console
  }

  function dwarn(...args) {
    if (!debug) return;
    // eslint-disable-next-line no-console
    console.warn('[dataLayer-events]', ...args);
  }

  /**
   * @param {any} cents
   */
  function centsToValue(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return undefined;
    return Number((n / 100).toFixed(2));
  }

  function getCurrency() {
    return (
      cfg.currency ||
      state.cart?.currency ||
      window.Shopify?.currency?.active ||
      'USD'
    );
  }

  function getCustomerId() {
    if (!sendCustomerId) return undefined;
    const id = cfg.customer?.id;
    return id != null ? String(id) : undefined;
  }

  function canTrack() {
    if (cfg.respectConsent !== true) return true;

    const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
    if (!consent || typeof consent !== 'object') return true;

    // If consent is explicitly denied, don't push events.
    const analytics = consent.analytics;
    const marketing = consent.marketing;
    if (analytics === false && marketing === false) return false;

    // Otherwise allow (covers "undefined" while API loads).
    return true;
  }

  function wrapDataLayerPushForDebug() {
    if (!debug) return;
    const dl = window.dataLayer;
    if (!dl || typeof dl.push !== 'function') return;
    if (dl.push.__datalayerEventsWrapped === true) return;

    const originalPush = dl.push.bind(dl);
    // eslint-disable-next-line no-param-reassign
    dl.push = function (...args) {
      try {
        // eslint-disable-next-line no-console
      } catch {
        // ignore
      }
      return originalPush(...args);
    };
    dl.push.__datalayerEventsWrapped = true;
  }

  /**
   * @param {any} raw
   */
  function getVariantId(raw) {
    const id = raw?.variant_id ?? raw?.id ?? raw?.variantId;
    return id != null ? String(id) : undefined;
  }

  /**
   * @param {any} raw
   */
  function getProductId(raw) {
    const id = raw?.product_id ?? raw?.productId;
    return id != null ? String(id) : undefined;
  }

  /**
   * @param {any} raw
   */
  function getItemKey(raw) {
    return raw?.key ? String(raw.key) : getVariantId(raw);
  }

  /**
   * @param {any} raw
   */
  function getQuantity(raw) {
    const q = raw?.quantity;
    const n = Number(q);
    if (!Number.isFinite(n) || n <= 0) return 1;
    return n;
  }

  /**
   * @param {any} raw
   */
  function getUnitPriceCents(raw) {
    const candidates = [
      raw?.final_price,
      raw?.price,
      raw?.original_price,
    ];
    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n)) return n;
    }

    const line = Number(raw?.final_line_price ?? raw?.line_price);
    const qty = getQuantity(raw);
    if (Number.isFinite(line) && qty > 0) return Math.round(line / qty);

    return undefined;
  }

  /**
   * @param {any} raw
   * @param {number} [quantityOverride]
   */
  function toGA4Item(raw, quantityOverride) {
    const variantId = getVariantId(raw);
    const productId = getProductId(raw);
    const sku = raw?.sku != null ? String(raw.sku) : undefined;

    const unitPriceCents = getUnitPriceCents(raw);
    const price = unitPriceCents != null ? centsToValue(unitPriceCents) : undefined;

    /** @type {any} */
    const item = {
      item_id: itemIdField === 'sku' && sku ? sku : variantId,
      item_name: raw?.product_title ?? raw?.productTitle ?? raw?.title ?? undefined,
      item_brand: raw?.vendor ?? undefined,
      item_category: raw?.product_type ?? raw?.productType ?? undefined,
      item_variant: raw?.variant_title ?? raw?.variantTitle ?? undefined,
      item_group_id: productId,
      price,
      quantity: quantityOverride ?? getQuantity(raw),
    };

    // Drop undefined fields to keep dataLayer clean.
    for (const k of Object.keys(item)) {
      if (item[k] === undefined) delete item[k];
    }

    return item;
  }

  /**
   * @param {any} raw
   * @param {number} [quantityOverride]
   */
  function toUAProduct(raw, quantityOverride) {
    const variantId = getVariantId(raw);
    const sku = raw?.sku != null ? String(raw.sku) : undefined;
    const id = itemIdField === 'sku' && sku ? sku : variantId;

    const unitPriceCents = getUnitPriceCents(raw);
    const price = unitPriceCents != null ? centsToValue(unitPriceCents) : undefined;

    /** @type {any} */
    const p = {
      id,
      name: raw?.product_title ?? raw?.productTitle ?? raw?.title ?? undefined,
      price,
      brand: raw?.vendor ?? undefined,
      category: raw?.product_type ?? raw?.productType ?? undefined,
      variant: raw?.variant_title ?? raw?.variantTitle ?? undefined,
      quantity: quantityOverride ?? getQuantity(raw),
    };

    for (const k of Object.keys(p)) {
      if (p[k] === undefined) delete p[k];
    }

    return p;
  }

  /**
   * @param {any[]} products
   */
  function uaDetail(products) {
    return {
      detail: {
        actionField: { list: '', action: 'detail' },
        products,
      },
    };
  }

  /**
   * @param {any[]} products
   */
  function uaAdd(products) {
    return { add: { products } };
  }

  /**
   * @param {any[]} products
   */
  function uaRemove(products) {
    return { remove: { products } };
  }

  /**
   * @param {any[]} products
   * @param {string} [option]
   */
  function uaCheckout(products, option) {
    return {
      checkout: {
        actionField: { step: 1, ...(option ? { option } : {}) },
        products,
      },
    };
  }

  /**
   * @param {string} eventName
   * @param {{ga4?: any, ua?: any}} payloads
   * @param {Record<string, any>} [extra]
   */
  function pushEcommerceEvent(eventName, payloads, extra) {
    if (!canTrack()) return;

    const primaryEcommerce = format === 'ua' ? payloads?.ua : payloads?.ga4;
    if (!primaryEcommerce) return;

    const payload = {
      event: eventName,
      page_type: cfg.pageType,
      customer_id: getCustomerId(),
      ...(extra || {}),
      ecommerce: primaryEcommerce,
      ...(format === 'both' && payloads?.ua ? { ecommerce_ua: payloads.ua } : {}),
    };

    // GTM recommendation: clear previous ecommerce object.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);

    dlog('pushed', eventName, payload);

    // SFCC-like: also call gtag('event', ...) for GA4 events (if enabled).
    if (gtagMirror && payloads?.ga4 && typeof window.gtag === 'function') {
      try {
        /** @type {any} */
        const params = {
          ...payloads.ga4,
          customer_id: getCustomerId(),
          page_type: cfg.pageType,
          ...(extra || {}),
        };

        // Drop undefined to keep payload clean.
        for (const k of Object.keys(params)) {
          if (params[k] === undefined) delete params[k];
        }

        window.gtag('event', eventName, params);
        dlog('gtag mirrored', eventName, params);
      } catch (err) {
        dwarn('gtag mirror failed', err);
      }
    }
  }

  /**
   * @param {any} cart
   */
  function toEcommerceFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return { currency: getCurrency(), items: [] };
    }

    const currency = cart.currency || getCurrency();
    const items = cart.items.map((i) => toGA4Item(i));
    const value = centsToValue(cart.total_price);

    /** @type {any} */
    const ecommerce = { currency, items };
    if (value != null) ecommerce.value = value;
    return ecommerce;
  }

  /**
   * @param {any} cart
   * @param {string} type
   */
  function toUAFromCart(cart, type) {
    const items = cart?.items;
    if (!Array.isArray(items)) {
      if (type === 'checkout') return uaCheckout([], 'checkout');
      return uaCheckout([], 'cart');
    }

    const products = items.map((i) => toUAProduct(i));
    if (type === 'checkout') return uaCheckout(products, 'checkout');
    return uaCheckout(products, 'cart');
  }

  /**
   * @param {any} oldCart
   * @param {any} newCart
   */
  function diffCartAndPush(oldCart, newCart) {
    if (!newCart || !Array.isArray(newCart.items)) return;

    /** @type {Map<string, any>} */
    const oldByKey = new Map();
    if (oldCart && Array.isArray(oldCart.items)) {
      for (const it of oldCart.items) {
        const key = getItemKey(it);
        if (key) oldByKey.set(key, it);
      }
    }

    /** @type {Map<string, any>} */
    const newByKey = new Map();
    for (const it of newCart.items) {
      const key = getItemKey(it);
      if (key) newByKey.set(key, it);
    }

    const keys = new Set([...oldByKey.keys(), ...newByKey.keys()]);
    for (const key of keys) {
      const oldItem = oldByKey.get(key);
      const newItem = newByKey.get(key);

      const oldQty = oldItem ? getQuantity(oldItem) : 0;
      const newQty = newItem ? getQuantity(newItem) : 0;
      const delta = newQty - oldQty;
      if (!delta) continue;

      if (delta > 0 && newItem) {
        dlog('cart diff add', { key, delta });
        const ga4Item = toGA4Item(newItem, delta);
        const uaProduct = toUAProduct(newItem, delta);
        const unitCents = getUnitPriceCents(newItem);
        const value =
          unitCents != null ? centsToValue(unitCents * delta) : undefined;

        pushEcommerceEvent(
          'add_to_cart',
          { ga4: { currency: getCurrency(), value, items: [ga4Item] }, ua: uaAdd([uaProduct]) },
          { cart_update_source: 'cart_change' }
        );
        continue;
      }

      if (delta < 0 && oldItem) {
        dlog('cart diff remove', { key, delta });
        const removedQty = Math.abs(delta);
        const ga4Item = toGA4Item(oldItem, removedQty);
        const uaProduct = toUAProduct(oldItem, removedQty);
        const unitCents = getUnitPriceCents(oldItem);
        const value =
          unitCents != null ? centsToValue(unitCents * removedQty) : undefined;

        pushEcommerceEvent(
          'remove_from_cart',
          { ga4: { currency: getCurrency(), value, items: [ga4Item] }, ua: uaRemove([uaProduct]) },
          { cart_update_source: 'cart_change' }
        );
      }
    }
  }

  async function refreshCartState() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = await res.json();
      if (cart && Array.isArray(cart.items)) state.cart = cart;
      dlog('cart refreshed', { item_count: cart?.item_count, total_price: cart?.total_price });
    } catch {
      dwarn('cart refresh failed');
      // ignore
    }
  }

  /**
   * @param {any} addedItem
   */
  function pushAddToCartFromAddedItem(addedItem) {
    if (!addedItem) return;

    const qty = getQuantity(addedItem);
    const ga4Item = toGA4Item(addedItem, qty);
    const uaProduct = toUAProduct(addedItem, qty);

    const unitCents = getUnitPriceCents(addedItem);
    const value = unitCents != null ? centsToValue(unitCents * qty) : undefined;

    dlog('add_to_cart from /cart/add.js', { variant_id: addedItem?.variant_id, quantity: qty, value });
    pushEcommerceEvent(
      'add_to_cart',
      { ga4: { currency: getCurrency(), value, items: [ga4Item] }, ua: uaAdd([uaProduct]) },
      isWishlistPage ? { cart_add_source: 'wishlist' } : undefined
    );

    if (isWishlistPage) {
      dlog('wishlist_to_cart', { variant_id: addedItem?.variant_id, quantity: qty, value });
      pushEcommerceEvent('wishlist_to_cart', {
        ga4: { currency: getCurrency(), value, items: [ga4Item] },
      });
    }
  }

  // ─── view_item_list ───────────────────────────────────────────────────────
  function initViewItemList() {
    const items = cfg.collectionItems;
    if (!Array.isArray(items) || items.length === 0) return;

    const ga4Items = items.map((raw, idx) => {
      const item = toGA4Item(raw);
      item.index = idx + 1;
      if (cfg.listName) item.item_list_name = cfg.listName;
      return item;
    });

    dlog('view_item_list', { count: ga4Items.length, list: cfg.listName });
    pushEcommerceEvent('view_item_list', {
      ga4: { item_list_name: cfg.listName || undefined, items: ga4Items },
    });
  }

  // ─── select_item ──────────────────────────────────────────────────────────
  function bindSelectItem() {
    document.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest(
          'a.product-card__link, product-card-link > a, .live-tv-product-card__image-link, .live-tv-product-card__title a'
        );
        if (!link) return;

        const card = link.closest('product-card, product-card-link, [data-product-id]');
        const productId = card?.dataset?.productId;
        if (!productId) return;

        const items = cfg.collectionItems;
        let raw = null;
        if (Array.isArray(items)) {
          raw = items.find((i) => String(i.product_id) === String(productId));
        }
        if (!raw) {
          const titleEl = card.querySelector('.visually-hidden, h3, .product-card__title');
          raw = { product_id: productId, variant_id: productId, product_title: titleEl?.textContent?.trim() };
        }

        const item = toGA4Item(raw);
        if (cfg.listName) item.item_list_name = cfg.listName;

        dlog('select_item', { productId });
        pushEcommerceEvent('select_item', {
          ga4: { item_list_name: cfg.listName || undefined, items: [item] },
        });
      },
      true
    );
  }

  // ─── add_to_wishlist ──────────────────────────────────────────────────────
  function bindAddToWishlist() {
    document.addEventListener('wishlist:updated', (e) => {
      const detail = /** @type {any} */ (e).detail;
      if (!detail || detail.action !== 'added') return;

      const productId = detail.productId ? String(detail.productId) : undefined;
      const variantId = detail.variantId ? String(detail.variantId) : undefined;

      let raw = null;
      if (Array.isArray(cfg.collectionItems)) {
        raw = cfg.collectionItems.find(
          (i) => String(i.variant_id) === variantId || String(i.product_id) === productId
        );
      }
      if (!raw && cfg.product?.variant) {
        const p = cfg.product;
        const v = p.variant;
        raw = {
          variant_id: variantId || String(v.id),
          product_id: productId || String(p.id),
          sku: v.sku,
          product_title: p.title,
          vendor: p.vendor,
          product_type: p.product_type,
          price: v.price,
        };
      }
      if (!raw) raw = { variant_id: variantId, product_id: productId };

      const item = toGA4Item(raw);
      const unitCents = getUnitPriceCents(raw);
      const value = unitCents != null ? centsToValue(unitCents) : undefined;

      dlog('add_to_wishlist', { productId, variantId });
      pushEcommerceEvent('add_to_wishlist', {
        ga4: { currency: getCurrency(), value, items: [item] },
      });
    });
  }

  // ─── search ───────────────────────────────────────────────────────────────
  function pushSearchEvent(term) {
    if (!term || !canTrack()) return;
    dlog('search', { term });
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'search',
      search_term: term,
      page_type: cfg.pageType,
      customer_id: getCustomerId(),
    });
    if (gtagMirror && typeof window.gtag === 'function') {
      // Intentionally ignored: a gtag failure must never break search.
        try { window.gtag('event', 'search', { search_term: term }); } catch {}
    }
  }

  function initSearch() {
    if (cfg.searchTerm != null) pushSearchEvent(cfg.searchTerm);
  }

  function bindSearchForm() {
    document.addEventListener('submit', (e) => {
      const form = /** @type {HTMLFormElement|null} */ (e.target);
      if (!form?.matches('form[action="/search"]')) return;
      const input = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="q"]'));
      const term = input?.value?.trim();
      if (term) pushSearchEvent(term);
    });
  }

  // ─── live_tv_click ────────────────────────────────────────────────────────
  function bindLiveTvClick() {
    document.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;
        const href = link.getAttribute('href') || '';
        const inLiveTvBlock = !!link.closest('.live-tv-content-block');
        if (inLiveTvBlock) return;

        const isLiveTvLink =
          href.includes('live-tv') ||
          href.includes('shoplc-tv') ||
          !!link.closest('[class*="livetv"]') ||
          !!link.closest('[class*="live-tv"]');
        if (!isLiveTvLink) return;

        const location = link.closest('nav, header, .header') ? 'navigation' : 'page';
        dlog('live_tv_click', { href, location });
        if (!canTrack()) return;
        window.dataLayer.push({
          event: 'live_tv_click',
          click_location: location,
          link_url: href,
          page_type: cfg.pageType,
          customer_id: getCustomerId(),
        });
      },
      true
    );
  }

  // ─── watch_live_clicked ───────────────────────────────────────────────────
  function bindWatchLiveClicked() {
    document.addEventListener(
      'click',
      (e) => {
        if (!e.target.closest('.live-tv-content-block')) return;
        if (!e.target.closest('a, button, iframe')) return;
        dlog('watch_live_clicked');
        if (!canTrack()) return;
        window.dataLayer.push({
          event: 'watch_live_clicked',
          page_type: cfg.pageType,
          customer_id: getCustomerId(),
        });
      },
      true
    );
  }

  // ─── loyalty_points_viewed ────────────────────────────────────────────────
  function initLoyaltyPointsViewed() {
    if (cfg.pageType !== 'page') return;
    const path = window.location.pathname.toLowerCase();
    const isRewardsPage =
      path.includes('reward') ||
      path.includes('loyalty') ||
      path.includes('punkte') ||
      cfg.template === 'page.rewards';
    if (!isRewardsPage) return;

    dlog('loyalty_points_viewed');
    if (!canTrack()) return;
    window.dataLayer.push({
      event: 'loyalty_points_viewed',
      customer_id: getCustomerId(),
      page_type: cfg.pageType,
    });
  }

  // ─── product_out_of_stock_view ────────────────────────────────────────────
  function initProductOutOfStockView() {
    if (cfg.pageType !== 'product' || !cfg.product?.variant) return;
    if (cfg.product.variant.available !== false) return;

    const p = cfg.product;
    const v = p.variant;
    dlog('product_out_of_stock_view', { variant_id: v.id });
    if (!canTrack()) return;
    window.dataLayer.push({
      event: 'product_out_of_stock_view',
      item_id: String(v.id),
      item_name: p.title,
      item_variant: v.title !== 'Default Title' ? v.title : undefined,
      item_group_id: String(p.id),
      page_type: cfg.pageType,
      customer_id: getCustomerId(),
    });
  }

  function bindVariantOutOfStockView() {
    if (cfg.pageType !== 'product') return;
    document.addEventListener('change', (e) => {
      const input = /** @type {HTMLInputElement|null} */ (e.target);
      if (!input?.matches('input[name="id"], select[name="id"]')) return;
      const variantId = input.value;
      if (!variantId) return;

      // Read available state from the variant option element (set by theme JS)
      const form = input.closest('form');
      const availableInput = form?.querySelector('[data-variant-available]');
      const available = availableInput
        ? availableInput.getAttribute('data-variant-available') !== 'false'
        : true;

      if (available) return;

      dlog('product_out_of_stock_view (variant change)', { variant_id: variantId });
      if (!canTrack()) return;
      window.dataLayer.push({
        event: 'product_out_of_stock_view',
        item_id: variantId,
        item_name: cfg.product?.title,
        item_group_id: cfg.product?.id ? String(cfg.product.id) : undefined,
        page_type: cfg.pageType,
        customer_id: getCustomerId(),
      });
    });
  }

  // ─── login_attempt / login_failed ─────────────────────────────────────────
  function bindLoginEvents() {
    const path = window.location.pathname.toLowerCase();
    const isLoginPage = path.includes('/account/login') || path.includes('sign-in');
    if (!isLoginPage) return;

    // login_attempt: click on any sign-in submit button or social login link
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('.js-sign-in-link, [type="submit"], button[name="commit"]');
        if (!btn) return;
        const method = btn.classList.contains('sign-in-page__social-btn--google')
          ? 'google'
          : btn.classList.contains('sign-in-page__social-btn--facebook')
            ? 'facebook'
            : 'email';
        dlog('login_attempt', { method });
        if (!canTrack()) return;
        window.dataLayer.push({
          event: 'login_attempt',
          method,
          page_type: cfg.pageType,
          customer_id: getCustomerId(),
        });
      },
      true
    );

    // login_failed: error block present in DOM on page load (Shopify renders errors server-side)
    const errorEl = document.querySelector(
      '.errors, .form--error, [data-login-error], .sign-in-page__error, #login-error'
    );
    if (errorEl) {
      const errorText = errorEl.textContent?.trim() || 'unknown error';
      dlog('login_failed', { error: errorText });
      if (canTrack()) {
        window.dataLayer.push({
          event: 'login_failed',
          method: 'email',
          error_message: errorText,
          page_type: cfg.pageType,
          customer_id: getCustomerId(),
        });
      }
    }
  }

  function initPageViewEvents() {
    // PDP view_item
    if (cfg.pageType === 'product' && cfg.product?.variant?.id) {
      const p = cfg.product;
      const v = cfg.product.variant;
      const raw = {
        variant_id: v.id,
        product_id: p.id,
        sku: v.sku,
        product_title: p.title,
        variant_title: v.title,
        vendor: p.vendor,
        product_type: p.product_type,
        price: v.price,
      };
      const item = toGA4Item(raw);
      const uaProduct = toUAProduct(raw);
      const value = centsToValue(v.price);
      dlog('init view_item', { variant_id: v.id, value });
      pushEcommerceEvent('view_item', {
        ga4: { currency: getCurrency(), value, items: [item] },
        ua: uaDetail([uaProduct]),
      });
    }

    // Cart page view_cart
    if (cfg.pageType === 'cart') {
      dlog('init view_cart (page)');
      pushEcommerceEvent('view_cart', {
        ga4: toEcommerceFromCart(state.cart),
        ua: toUAFromCart(state.cart, 'cart'),
      });
    }
  }

  function bindCartDrawerViewCart() {
    document.addEventListener('cart-drawer:open', async () => {
      await refreshCartState();
      dlog('view_cart (drawer open)');
      pushEcommerceEvent(
        'view_cart',
        {
          ga4: toEcommerceFromCart(state.cart),
          ua: toUAFromCart(state.cart, 'cart'),
        },
        { cart_view_source: 'drawer' }
      );
    });
  }

  function bindBeginCheckout() {
    document.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;

        const link = t.closest('a[href]');
        const href = link?.getAttribute('href') || '';
        if (!href) return;

        // Shopify checkout can be /checkout or /checkout?...
        if (!href.startsWith('/checkout')) return;

        dlog('begin_checkout click', { href });
        pushEcommerceEvent('begin_checkout', {
          ga4: toEcommerceFromCart(state.cart),
          ua: toUAFromCart(state.cart, 'checkout'),
        });
      },
      true
    );
  }

  function wrapFetch() {
    const originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    // True from the moment /cart/add.js returns until refreshCartState() has written
    // the post-add cart into state.cart.
    //
    // refreshCartState() is async and nothing used to wait for it. Apps that mutate
    // the cart immediately after an add - loyalty, automatic discounts, free-gift,
    // all of which only run for logged-in customers - would hit the mutation branch
    // below while state.cart was still the PRE-ADD cart. The diff then saw the item
    // just added as a fresh addition and pushed a second add_to_cart, tagged
    // cart_update_source: 'cart_change'. That is the logged-in double-fire.
    let addSettling = false;

    window.fetch = async function (...args) {
      const res = await originalFetch.apply(this, args);

      try {
        const req = args[0];
        let url = '';
        if (typeof req === 'string') url = req;
        else if (req instanceof Request) url = req.url;
        else if (req instanceof URL) url = req.toString();
        else url = String(req || '');

        if (!url) return res;

        // Only handle same-origin cart endpoints.
        const path = new URL(url, location.origin).pathname;
        if (debug && (path.startsWith('/cart') || path === '/checkout')) {
          dlog('fetch', { path, ok: res.ok, status: res.status });
        }

        // Keep cart state fresh when theme fetches it.
        if (path === '/cart.js' || path === '/cart.json') {
          const clone = res.clone();
          clone
            .json()
            .then((cart) => {
              if (cart && Array.isArray(cart.items)) state.cart = cart;
            })
            .catch((err) => {
              // Swallowed so analytics never breaks the cart, but reported:
              // a stale state.cart silently distorts begin_checkout/view_cart.
              console.warn('[datalayer] could not refresh cart state from /cart.js', err);
            });
          return res;
        }

        if (path === '/cart/add.js') {
          const clone = res.clone();
          addSettling = true;
          clone
            .json()
            .then((item) => {
              pushAddToCartFromAddedItem(item);
              // Update state cart opportunistically (no diff) for begin_checkout/view_cart.
              // Returned so the guard below clears only once state.cart is current.
              return refreshCartState();
            })
            .catch((err) => {
              console.warn('[datalayer] add-to-cart event could not be pushed', err);
            })
            .finally(() => {
              addSettling = false;
            });

          // THE MINI CART IS DEPRECATED HERE ON PURPOSE - DO NOT REINSTATE.
          //
          // This wrapper used to call window.ShopLC.openCartDrawer() on every ok /cart/add.js,
          // to cover app-embedded add sources (the Live TV carousels, wishlist, regios). The
          // block in layout/theme.liquid now owns opening, and its coverage is strictly wider:
          // it wraps XMLHttpRequest as well as fetch, matches locale-prefixed URLs such as
          // /de/cart/add.js (the check here was an exact path === '/cart/add.js'), handles the
          // deferred drawer's async opener, and records what the shopper has actually been shown.
          //
          // Crucially, it also distinguishes the shopper's own add from an app reconciling the
          // cart with nobody touching the page. This call could not, so it re-opened the drawer
          // for regios/BOGOS adds fired by a poll or on returning from checkout - reported
          // 2026-09-02 - defeating that guard entirely, because it runs in the same response.
          //
          // Removing it changes no analytics: add_to_cart is pushed in the promise chain above,
          // and view_cart is pushed by the 'cart-drawer:open' listener when the drawer actually
          // opens. cart-drawer.js's open() dispatches that event unconditionally, so having two
          // ungated openers double-fired view_cart on a genuine add, depending on which won the
          // race. One owner means exactly one view_cart.

          return res;
        }

        const isCartMutation =
          path === '/cart/change.js' ||
          path === '/cart/update.js' ||
          path === '/cart/clear.js';

        if (!isCartMutation) return res;

        const oldCart = state.cart;
        const clone = res.clone();

        // Read synchronously: by the time the promise below resolves the guard may
        // already have cleared, but what matters is whether an add was settling at
        // the moment this mutation was observed.
        const settlingAnAdd = addSettling;

        clone
          .json()
          .then((cart) => {
            if (!cart || !Array.isArray(cart.items)) return;
            state.cart = cart;

            // A mutation landing mid-add means oldCart is the pre-add cart, so the
            // diff cannot tell "app changed the cart" from "user just added". Take
            // the fresh cart as the new baseline and skip the diff rather than emit
            // a duplicate add_to_cart. The genuine add was already reported by
            // pushAddToCartFromAddedItem, so nothing is lost.
            if (settlingAnAdd) {
              dlog('cart mutation during add settle - state updated, diff skipped');
              return;
            }

            diffCartAndPush(oldCart, cart);
          })
          .catch((err) => {
            console.warn('[datalayer] cart diff could not be pushed', err);
          });
      } catch {
        // ignore
      }

      return res;
    };
  }

  // Expose a tiny API for debugging / manual pushes.
  window.ShopLCDataLayer = {
    pushEcommerceEvent,
    refreshCartState,
    getCart: () => state.cart,
  };

  wrapDataLayerPushForDebug();
  dlog('init', {
    pageType: cfg.pageType,
    format,
    gtagMirror,
    respectConsent: cfg.respectConsent === true,
    canTrack: canTrack(),
    consent: window.Shopify?.customerPrivacy?.currentVisitorConsent?.(),
  });

  wrapFetch();
  bindCartDrawerViewCart();
  bindBeginCheckout();
  initPageViewEvents();

  // New events
  initViewItemList();
  bindSelectItem();
  bindAddToWishlist();
  initSearch();
  bindSearchForm();
  bindLiveTvClick();
  bindWatchLiveClicked();
  initLoyaltyPointsViewed();
  initProductOutOfStockView();
  bindVariantOutOfStockView();
  bindLoginEvents();
})();
